import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Set<number>} */
  #userSelectedFieldsets = new Set();

  /**
   * Tracks the option value name (e.g. "Large") that the user has explicitly selected per fieldset.
   * Keyed by fieldset index; only populated for fieldsets 1+ (fieldset 0 is always server-managed).
   * Persists across combined listing product switches so matching values can be restored.
   * @type {Map<number, string>}
   */
  #userSelectedOptions = new Map();

  /**
   * Tracks fieldsets that should remain unselected after option 1 changes to incompatible values.
   * Prevents auto-selection of first available option when option 1 changes and current selection is unavailable.
   * @type {Set<number>}
   */
  #forceUnselectedFieldsets = new Set();

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const noPreselect = this.dataset.noPreselectOptions === 'true';

    // Single-fieldset products (e.g. a Size-only product) require the user to pick explicitly.
    // Multi-fieldset products treat fieldset 0 as the "context" (Quote) pre-selected by the URL.
    const isSingleFieldset = fieldsets.length === 1;

    fieldsets.forEach((fieldset, index) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');

      // For noPreselect: push [] for any fieldset that will be cleared (index > 0, or index 0 on
      // single-fieldset products). This enables the pill animation on the first user selection.
      if (noPreselect && (index > 0 || isSingleFieldset)) {
        this.#checkedIndices.push([]);
      } else if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
        // Seed #userSelectedOptions for fieldsets 1+ so that when option 1 changes,
        // the current selection can be checked for availability and force-unselected if needed.
        if (index > 0 && radios[initialCheckedIndex]) {
          this.#userSelectedOptions.set(index, radios[initialCheckedIndex].value);
        }
      }
    });

    if (noPreselect) {
      // Multi-fieldset only: fieldset 0 is the Quote/context, always treated as user-selected.
      // Single-fieldset products must wait for an explicit user pick before ATC is enabled.
      if (!isSingleFieldset) {
        this.#userSelectedFieldsets.add(0);
      }
      this.#applyNoPreselectState();
    }

    // Auto-select fieldsets with exactly one total option — runs for all products so the live
    // picker in a combined listing morph (which loses data-no-preselect-options) also fires.
    // For regular products this is a harmless no-op (server already pre-selects everything).
    this.#autoSelectSingleOptions();

    if (noPreselect) {
      // Disable the buy button on initial load — the Liquid layer may not have done so if the
      // metafield check is evaluated before the request URL resolves. Defer until after all
      // custom elements (product-form-component) have connected and attached their listeners.
      requestYieldCallback(() => {
        // Always dispatch so product-form-component can set the correct ATC button state.
        // On single-fieldset products selectedOptionId may be undefined (all radios cleared),
        // so we fall back to '' — the important signal is resource=null disabling the button.
        const sourceId = this.selectedOptionId ?? '';
        const textContent = this.querySelector('script[type="application/json"]')?.textContent;
        const resource = this.#allOptionsSelected && textContent && sourceId ? JSON.parse(textContent) : null;
        this.dispatchEvent(
          new VariantUpdateEvent(resource, sourceId, {
            html: new DOMParser().parseFromString('', 'text/html'),
            productId: this.dataset.productId ?? '',
            allOptionsSelected: this.#allOptionsSelected,
            firstUnselectedOptionName: this.#firstUnselectedOptionName,
          })
        );
      });
    }

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const fieldsetIndex = Number.parseInt(
      /** @type {HTMLElement | null} */ (event.target.closest('[data-fieldset-index]'))?.dataset.fieldsetIndex ?? ''
    );

    if (!Number.isNaN(fieldsetIndex) && event.isTrusted) {
      // Clear any force-unselected flag when user makes a selection on this fieldset
      // But don't clear it when user is changing option 1, as that's when we want to force unselect other options
      if (fieldsetIndex > 0 && this.#forceUnselectedFieldsets.has(fieldsetIndex)) {
        this.#forceUnselectedFieldsets.delete(fieldsetIndex);
      }
      this.#userSelectedFieldsets.add(fieldsetIndex);
      const value =
        selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement
          ? selectedOption.value
          : '';

      const fieldsetCount = (this.refs.fieldsets || []).length;
      if (value && (fieldsetIndex > 0 || fieldsetCount === 1)) {
        // Store the selected value so #applyNoPreselectState can restore it after a morph.
        // Covers both secondary fieldsets (index > 0) and single-fieldset products (index 0).
        this.#userSelectedOptions.set(fieldsetIndex, value);
      } else {
        // Immediately deselect option 2+ when option 1 changes, for instant visual feedback
        // before the fetch response arrives.
        //
        // For combined listing product switches we can't know the new product's availability,
        // so we always clear the visual state. #userSelectedOptions is intentionally kept so
        // that #applyNoPreselectState can restore the value if it's available in the new product.
        //
        // For same-product option 1 changes we clear only if the value is unavailable in the
        // current DOM, and also forget the saved value since it's ruled out for this product.
        const connectedUrl = selectedOption instanceof HTMLElement ? selectedOption.dataset.connectedProductUrl : undefined;
        const isSwitchingProduct = !!connectedUrl && connectedUrl !== this.dataset.productUrl?.split('?')[0];

        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        fieldsets.forEach((fieldset, idx) => {
          if (idx === 0) return;
          const userValue = this.#userSelectedOptions.get(idx);

          // When switching products, always mark fieldsets 1+ for force unselection
          // to prevent server-side auto-selection, even if there's no userValue
          if (isSwitchingProduct) {
            this.#forceUnselectedFieldsets.add(idx);
            return;
          }

          if (!userValue) {
            return;
          }
          const radios = Array.from(fieldset.querySelectorAll('input'));
          const match = /** @type {HTMLInputElement | undefined} */ (
            radios.find((input) => /** @type {HTMLInputElement} */ (input).value === userValue)
          );
          const unavailableInCurrentDom = !match || match.getAttribute('aria-disabled') === 'true';

          if (unavailableInCurrentDom) {
            this.#userSelectedOptions.delete(idx);
            this.#userSelectedFieldsets.delete(idx);
            // Mark this fieldset to remain unselected to prevent auto-selection after morph
            this.#forceUnselectedFieldsets.add(idx);
            for (const radio of radios) {
              /** @type {HTMLInputElement} */ (radio).checked = false;
              /** @type {HTMLElement} */ (radio).dataset.currentChecked = 'false';
            }
            const legendValue = fieldset.querySelector('legend .variant-option__swatch-value');
            if (legendValue) legendValue.textContent = '';
          }
        });
      }
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Returns the variant id to request the section with instead of `option_values`, or undefined
   * when `option_values` should be used.
   *
   * Some legacy combined-listing products expose option value ids that Shopify's `option_values`
   * query parameter does not accept — a request built from them silently resolves back to the
   * product's first variant, so every selection in the affected option snaps back. Liquid flags
   * those pickers with `data-unreliable-option-values`; for them we request the concrete variant
   * instead, which Shopify always resolves correctly.
   *
   * Only used when the whole picker resolves to a single variant on the current product:
   * `variant` cannot express a partial selection (no-preselect) or a combined listing switch.
   *
   * @param {HTMLElement} selectedOption - The option the customer just picked.
   * @returns {string | undefined} The variant id, or undefined to fall back to `option_values`.
   */
  #variantIdRequestParam(selectedOption) {
    if (this.dataset.unreliableOptionValues !== 'true') return undefined;

    const { variantId, connectedProductUrl } = selectedOption.dataset;
    if (!variantId) return undefined;

    // A combined listing switch renders a different product; keep `option_values` so the server
    // can carry over whichever of the previous product's option values it recognises.
    if (connectedProductUrl && connectedProductUrl !== this.dataset.productUrl?.split('?')[0]) {
      return undefined;
    }

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    if (!fieldsets.every((fieldset) => fieldset.querySelector('input:checked'))) return undefined;

    return variantId;
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    const variantIdParam = source ? undefined : this.#variantIdRequestParam(selectedOption);

    if (variantIdParam) {
      // `variant` replaces `option_values` rather than joining it — when both are present
      // Shopify resolves from `option_values` and ignores `variant`.
      params.push(`variant=${variantIdParam}`);
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      const priceElement = this.closest('product-card')?.querySelector('product-price') || this.closest('product-price');
      const sectionParams = [];

      if (priceElement?.dataset.compareAtBelowSaleDesktop === 'true') {
        sectionParams.push('price_compare_at_below_sale_desktop=1');
      }

      const gapValue = priceElement?.dataset.compareAtBelowSaleDesktopGap;
      if (gapValue && gapValue !== '0') {
        sectionParams.push(`price_compare_at_below_sale_desktop_gap=${encodeURIComponent(gapValue)}`);
      }

      const base = `${productUrl}?section_id=section-rendering-product-card`;
      const optionParams = params.length ? `&${params.join('&')}` : '';
      const priceParams = sectionParams.length ? `&${sectionParams.join('&')}` : '';

      return `${base}${optionParams}${priceParams}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Returns true when all option fieldsets have been explicitly selected by the user.
   * Always true for non-no-preselect products.
   * @returns {boolean}
   */
  get #allOptionsSelected() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    // Only honour force-unselected flags that point at a fieldset on the CURRENT product.
    // After a combined-listing switch the set can still hold indices from a product with more
    // options (e.g. Size/Color) that don't exist on this one — those are stale and must be
    // ignored, otherwise a product with fewer options (or none) is wrongly treated as
    // "not all options selected" and its Add to Bag button stays disabled.
    for (const index of this.#forceUnselectedFieldsets) {
      if (index < fieldsets.length) return false;
    }
    if (this.dataset.noPreselectOptions !== 'true') return true;
    return fieldsets.every((_, i) => this.#userSelectedFieldsets.has(i));
  }

  /**
   * Returns the display name of the first secondary fieldset (index > 0) that the user
   * has not yet explicitly selected. Used to set the ATC button text to "Choose Color",
   * "Choose Size", etc. Returns undefined when all options are selected.
   * @returns {string | undefined}
   */
  get #firstUnselectedOptionName() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    // Start from 0: for single-fieldset products fieldset 0 itself needs to be chosen.
    // For multi-fieldset products fieldset 0 is always in #userSelectedFieldsets so it's skipped.
    for (let i = 0; i < fieldsets.length; i++) {
      if (!this.#userSelectedFieldsets.has(i)) {
        return fieldsets[i].dataset.optionName;
      }
    }
    return undefined;
  }

  /**
   * Returns true when the given variant resource represents exactly the combination of
   * option values currently checked in the DOM. Used after a combined-listing product
   * switch: the fetched page's variant JSON was rendered from the PRE-switch option_values
   * (which may reference the old product's value IDs), so it can be null or point at a
   * different variant than the one auto-select just completed client-side.
   * @param {{ options?: string[] } | null} resource - Parsed variant JSON.
   * @returns {boolean}
   */
  #resourceMatchesSelection(resource) {
    if (!resource || !Array.isArray(resource.options)) return false;
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const checkedValues = fieldsets.map(
      (fieldset) =>
        /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value ??
        /** @type {HTMLOptionElement | null} */ (fieldset.querySelector('select option[selected]'))?.value ??
        null
    );
    if (checkedValues.some((value) => value === null)) return false;
    return (
      resource.options.length === checkedValues.length &&
      resource.options.every((value, index) => value === checkedValues[index])
    );
  }

  /**
   * Handles force unselection of fieldsets after morph operations.
   * This works regardless of noPreselect setting to prevent auto-selection
   * when option 1 changes to incompatible values.
   */
  #applyForceUnselectionState() {
    if (this.#forceUnselectedFieldsets.size === 0) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#forceUnselectedFieldsets.forEach((index) => {
      if (index === 0) return;

      const fieldset = fieldsets[index];
      if (!fieldset) return;

      // Add a class to force unselected visual state
      fieldset.classList.add('variant-option--force-unselected');

      const radios = Array.from(fieldset.querySelectorAll('input'));
      radios.forEach((radio) => {
        const inputEl = /** @type {HTMLInputElement} */ (radio);
        // More aggressive clearing
        inputEl.checked = false;
        inputEl.removeAttribute('checked');
        inputEl.dataset.currentChecked = 'false';
        inputEl.dataset.previousChecked = 'false';
        // Add a data attribute to override CSS
        inputEl.dataset.forceUnselected = 'true';
        if (inputEl.parentElement) {
          inputEl.parentElement.classList.remove('variant-option__button-label--selected');
          inputEl.parentElement.classList.add('variant-option__button-label--force-unselected');
          // Directly set the --clip property to hide the selected border
          inputEl.parentElement.style.setProperty('--clip', '0 0 0 100%');
        }
      });
      this.#userSelectedFieldsets.delete(index);
      if (this.#checkedIndices[index]) {
        this.#checkedIndices[index] = [];
      }
      // Clear fieldset CSS properties that control pill animation
      if (fieldset) {
        fieldset.style.removeProperty('--pill-width-current');
        fieldset.style.removeProperty('--pill-width-previous');
        fieldset.style.setProperty('--pill-width-current', '0px');
        fieldset.style.setProperty('--pill-width-previous', '0px');
      }
      const legendValue = fieldset.querySelector('legend .variant-option__swatch-value');
      if (legendValue) legendValue.textContent = '';

      // Remove the force unselected class after a delay to allow CSS to update
      setTimeout(() => {
        // Uncheck ALL disabled options in ALL fieldsets (not just force unselected ones)
        // The server sometimes checks all unavailable options, so we need to clear them all
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        fieldsets.forEach((fieldset, index) => {
          if (index === 0) return;
          // Skip fieldsets with a deliberate selection (user pick or auto-select) — this sweep
          // must not clobber a valid selection made between scheduling and firing. Auto-select
          // can legitimately check an aria-disabled radio when it's the fieldset's only option.
          if (this.#userSelectedFieldsets.has(index)) return;
          const radios = Array.from(fieldset.querySelectorAll('input'));
          radios.forEach((radio) => {
            const inputEl = /** @type {HTMLInputElement} */ (radio);
            // Uncheck any disabled option that got checked by the server
            if (inputEl.checked && inputEl.getAttribute('aria-disabled') === 'true') {
              inputEl.checked = false;
              inputEl.removeAttribute('checked');
              if (inputEl.parentElement) {
                inputEl.parentElement.style.setProperty('--clip', '0 0 0 100%');
              }
            }
          });
        });

        // Re-apply force unselection to ensure no variant is selected
        this.#forceUnselectedFieldsets.forEach((index) => {
          if (index === 0) return;
          const fieldset = fieldsets[index];
          if (!fieldset) return;

          const radios = Array.from(fieldset.querySelectorAll('input'));
          radios.forEach((radio) => {
            const inputEl = /** @type {HTMLInputElement} */ (radio);
            if (inputEl.checked) {
              inputEl.checked = false;
              inputEl.removeAttribute('checked');
            }
            // Also clear the visual clip property
            if (inputEl.parentElement) {
              inputEl.parentElement.style.setProperty('--clip', '0 0 0 100%');
            }
          });
        });

        // Clean up force unselected classes.
        // Only hide the selection indicator (--clip) if the radio is NOT checked — auto-select
        // may have re-checked it between now and when #applyForceUnselectionState() first ran,
        // and we must not override that visible state.
        fieldset.classList.remove('variant-option--force-unselected');
        radios.forEach((radio) => {
          const inputEl = /** @type {HTMLInputElement} */ (radio);
          inputEl.removeAttribute('data-force-unselected');
          if (inputEl.parentElement) {
            inputEl.parentElement.classList.remove('variant-option__button-label--force-unselected');
            if (!inputEl.checked) {
              inputEl.parentElement.style.setProperty('--clip', '0 0 0 100%');
            } else {
              // Radio was auto-selected; clear any force-unselect clip so CSS shows it selected.
              inputEl.parentElement.style.removeProperty('--clip');
            }
          }
        });
      }, 100);
    });
  }

  /**
   * For no-preselect products, automatically selects any secondary fieldset (index > 0) that has
   * exactly one available option. This covers combo products where a color option exists but is
   * not really a customer choice — the single available color should be pre-selected so the ATC
   * button is enabled immediately without requiring unnecessary interaction.
   */
  #autoSelectSingleOptions() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset, index) => {
      // Skip fieldset 0 only when it's the Quote context (multi-fieldset product).
      // For single-fieldset products fieldset 0 is NOT in #userSelectedFieldsets so it
      // can be auto-selected when exactly one option is available.
      if (index === 0 && this.#userSelectedFieldsets.has(0)) return;

      // Query the live DOM — this.#radios may hold stale references after updateVariantPicker()
      // morphs the picker, causing aria-disabled checks on detached elements to return wrong results.
      const radios = Array.from(/** @type {NodeListOf<HTMLInputElement>} */ (fieldset.querySelectorAll('input')));
      const availableRadios = radios.filter((r) => r.getAttribute('aria-disabled') !== 'true');

      // A fieldset with exactly ONE option in total is not a real choice — always select it,
      // even when that option is OOS (aria-disabled). Selecting it lets the variant resolve,
      // so the ATC button shows "Sold out" (and notify-me widgets can attach) instead of
      // being stuck on "Choose options" with nothing left to choose.
      // For multi-option fieldsets, auto-select only when exactly one option is AVAILABLE;
      // with several available options the customer must choose explicitly.
      const radio = radios.length === 1 ? radios[0] : availableRadios.length === 1 ? availableRadios[0] : undefined;
      if (!radio) return;
      const inputIndex = radios.indexOf(radio);

      radio.checked = true;
      radio.dataset.currentChecked = 'true';
      // Remove any inline --clip set by force-unselection so CSS can show the selection indicator.
      if (radio.parentElement) {
        radio.parentElement.style.removeProperty('--clip');
      }

      // Sync cached state so updateSelectedOption() and #allOptionsSelected stay consistent.
      this.#radios[index] = radios;
      // Always update (not conditional) — #checkedIndices[index] may be undefined (sparse) when
      // auto-select is called on a non-noPreselect product whose connectedCallback skipped the push.
      this.#checkedIndices[index] = [inputIndex];

      this.#userSelectedFieldsets.add(index);
      this.#userSelectedOptions.set(index, radio.value);
      // Remove any force-unselect flag so #allOptionsSelected can return true.
      this.#forceUnselectedFieldsets.delete(index);

      const legendValue = fieldset.querySelector('legend .variant-option__swatch-value');
      if (legendValue) legendValue.textContent = radio.value;
    });
  }

  /**
   * Enforces no-preselect state after any morph by applying or clearing user selections.
   * This is the single authoritative function for no-preselect checked state:
   * - Fieldset 0: skipped (always server-managed).
   * - Fieldsets 1+: if the user previously selected a value (tracked in #userSelectedOptions),
   *   find the matching available input and check it; otherwise uncheck all inputs in that fieldset.
   *
   * Must be called after every updateVariantPicker() or #reinitializeNoPreselect() because
   * the morph can overwrite checked states even when #allOptionsSelected is true.
   */
  #applyNoPreselectState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset, index) => {
      // Skip fieldset 0 only when it's the Quote context in a multi-fieldset product.
      // For single-fieldset products we must always process fieldset 0 — either to
      // restore the user's stored selection (userValue set) or to clear it (no selection yet).
      if (index === 0 && this.#userSelectedFieldsets.has(0) && fieldsets.length > 1) return;

      // If this fieldset is marked to remain unselected, clear it completely
      if (this.#forceUnselectedFieldsets.has(index)) {
        const radios = Array.from(fieldset.querySelectorAll('input'));
        for (const radio of radios) {
          const inputEl = /** @type {HTMLInputElement} */ (radio);
          inputEl.checked = false;
          inputEl.removeAttribute('checked');
          inputEl.dataset.currentChecked = 'false';
        }
        this.#userSelectedFieldsets.delete(index);
        if (this.#checkedIndices[index]) this.#checkedIndices[index] = [];
        const legendValue = fieldset.querySelector('legend .variant-option__swatch-value');
        if (legendValue) legendValue.textContent = '';
        // Don't clear the flag here - let it persist until user makes a selection
        return;
      }

      const userValue = this.#userSelectedOptions.get(index);
      const radios = Array.from(fieldset.querySelectorAll('input'));
      let matchedIndex = -1;

      radios.forEach((radio, i) => {
        const inputEl = /** @type {HTMLInputElement} */ (radio);
        const isMatch =
          !!userValue &&
          inputEl.value === userValue &&
          inputEl.getAttribute('aria-disabled') !== 'true';
        inputEl.checked = isMatch;
        // Remove the HTML attribute so defaultChecked doesn't allow a form reset to re-check it
        if (!isMatch) inputEl.removeAttribute('checked');
        inputEl.dataset.currentChecked = isMatch ? 'true' : 'false';
        if (isMatch) matchedIndex = i;
      });

      if (matchedIndex !== -1) {
        this.#userSelectedFieldsets.add(index);
        if (this.#checkedIndices[index]) this.#checkedIndices[index] = [matchedIndex];
      } else {
        this.#userSelectedFieldsets.delete(index);
        if (this.#checkedIndices[index]) this.#checkedIndices[index] = [];
        const legendValue = fieldset.querySelector('legend .variant-option__swatch-value');
        if (legendValue) legendValue.textContent = '';
      }
    });
  }

  /**
   * Re-initializes no-preselect state after a combined listing product switch.
   * connectedCallback does not re-run after a morph, so this duplicates the relevant
   * setup from connectedCallback: resets internal radio/index tracking, unchecks
   * option groups beyond the first, and marks only fieldset 0 as user-selected.
   */
  #reinitializeNoPreselect() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios = [];
    this.#checkedIndices = [];
    this.#userSelectedFieldsets = new Set([0]); // option 1 is pre-selected by navigation context
    // #forceUnselectedFieldsets is intentionally NOT reset — it carries the flags set in
    // variantChanged() so that #applyNoPreselectState() can honour them and keep option 2+
    // unselected when the previously-selected value is unavailable in the new product.
    //
    // Secondary selections (fieldsets 1+) from the previous combined-listing product MUST NOT
    // carry over. No-preselect requires an explicit choice per product, but #userSelectedOptions
    // persists across switches — so a size/colour that was chosen (or auto-selected for a
    // single-value option) on the previous product would silently re-apply here, enabling Add to
    // Bag without a real selection. Drop them; fieldset 0 (the navigation context) is preserved.
    for (const fieldsetIndex of [...this.#userSelectedOptions.keys()]) {
      if (fieldsetIndex > 0) this.#userSelectedOptions.delete(fieldsetIndex);
    }

    fieldsets.forEach((fieldset, index) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      if (index > 0) {
        this.#checkedIndices.push([]);
      } else {
        const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
        this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
      }
    });

    this.#applyNoPreselectState();
    this.#autoSelectSingleOptions();
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Read no-preselect from the fetched HTML's variant-picker, not from `this`.
        // The full-page morph strips data-no-preselect-options from the live element
        // (product.id !== product_resource.id in the section render context), so
        // this.dataset.noPreselectOptions reflects the *source* product, not the destination.
        const newVariantPickerEl = html.querySelector('variant-picker');
        const noPreselect = newVariantPickerEl instanceof HTMLElement
          ? newVariantPickerEl.dataset.noPreselectOptions === 'true'
          : this.dataset.noPreselectOptions === 'true';

        if (shouldMorphMain) {
          this.updateMain(html);

          // Klaviyo BIS compatibility: toggling the show-more button twice forces
          // Klaviyo's onsite script to re-detect the new product's variant state
          // after the full-page morph replaces its DOM hooks.
          try {
            const variantPickerToggle = /** @type {HTMLElement | null} */ (document.querySelector('.variant-picker__toggle'));
            if (variantPickerToggle) {
              const scrollY = window.scrollY;
              variantPickerToggle.click();
              variantPickerToggle.click();
              window.scrollTo({ top: scrollY, behavior: 'instant' });
            }
          } catch (e) {
            console.warn('Failed to toggle variants for Klaviyo BIS', { e });
          }

          // Apply force unselection regardless of noPreselect setting
          this.#applyForceUnselectionState();

          // connectedCallback does not re-run after a morph, so re-apply no-preselect state
          // manually when switching to a product that requires it (e.g., combined listing switch).
          // Deferred via requestYieldCallback so refs are fresh (morph queues updatedCallback via
          // queueMicrotask, which refreshes refs before requestYieldCallback fires).
          //
          // Note: noPreselect reflects the fetched HTML's variant-picker, but the full-page morph
          // strips data-no-preselect-options (product.id != product_resource.id in section context).
          // So we always run this — the live picker's #forceUnselectedFieldsets and
          // #allOptionsSelected will determine whether resource is null (button disabled).
          requestYieldCallback(() => {
            // After updateMain(), `this` is detached — the morph replaced the entire DOM.
            // Find the live variant-picker that replaced us and operate on that instead.
            const liveVariantPicker = /** @type {VariantPicker | null} */ (
              document.querySelector('variant-picker')
            );
            if (!liveVariantPicker) return;

            // Transfer force-unselected state from the detached picker to the live one.
            // variantChanged() populated #forceUnselectedFieldsets on `this` before the morph;
            // the live picker starts fresh so we copy the set across.
            for (const idx of this.#forceUnselectedFieldsets) {
              liveVariantPicker.#forceUnselectedFieldsets.add(idx);
            }

            if (noPreselect) {
              liveVariantPicker.#reinitializeNoPreselect();
            }

            // Always auto-select fieldsets with exactly one available option on the live picker.
            // Must run unconditionally: for combined-listing full-page morphs the
            // data-no-preselect-options attribute is stripped from the fetched HTML
            // (product.id !== product_resource.id in section render context), so noPreselect
            // is false and #reinitializeNoPreselect() is skipped above — but single-option
            // color/size fields still need to be auto-selected.
            liveVariantPicker.#autoSelectSingleOptions();

            // Sync the live picker's updated force-unselected set back to the detached picker.
            // #applyForceUnselectionState() was called on `this` (detached) but its refs still
            // point to the live DOM (morph is in-place), so its pending 100ms timeout will
            // re-uncheck any auto-selected inputs unless we clear the matching indices here.
            this.#forceUnselectedFieldsets = new Set(liveVariantPicker.#forceUnselectedFieldsets);

            if (liveVariantPicker.selectedOptionId) {
              const resource = liveVariantPicker.#allOptionsSelected ? JSON.parse(textContent) : null;

              // The fetched page was requested with the PRE-switch option_values, which can
              // reference the old product's value IDs. The server then renders `null` (or a
              // fallback variant) into the JSON script tag even though auto-select has since
              // completed the selection client-side. In that case, re-fetch the section with
              // the now-complete selection — the non-main path will dispatch the real variant.
              if (liveVariantPicker.#allOptionsSelected && !liveVariantPicker.#resourceMatchesSelection(resource)) {
                const { selectedOption } = liveVariantPicker;
                if (selectedOption) {
                  liveVariantPicker.fetchUpdatedSection(liveVariantPicker.buildRequestUrl(selectedOption));
                  return;
                }
              }

              const newProductId = liveVariantPicker.dataset.productId ?? '';
              const priceMin = liveVariantPicker.#allOptionsSelected ? null : liveVariantPicker.dataset.priceMin || null;
              liveVariantPicker.dispatchEvent(
                new VariantUpdateEvent(resource, liveVariantPicker.selectedOptionId, {
                  html: new DOMParser().parseFromString('', 'text/html'),
                  productId: newProductId,
                  newProduct: { id: newProductId, url: liveVariantPicker.dataset.productUrl ?? '' },
                  allOptionsSelected: liveVariantPicker.#allOptionsSelected,
                  priceMin,
                  firstUnselectedOptionName: liveVariantPicker.#firstUnselectedOptionName,
                })
              );
            }
          });
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Apply force unselection regardless of noPreselect setting
          this.#applyForceUnselectionState();

          const noPreselect = this.dataset.noPreselectOptions === 'true';

          // Always enforce user selection state after morph — the morph can overwrite checked
          // states from the server even when #allOptionsSelected is true.
          if (noPreselect) {
            this.#applyNoPreselectState();
          }

          // Auto-select single-option fieldsets unconditionally so it also fires when noPreselect
          // is false (e.g. the live picker in a combined listing morph that lost the attribute).
          this.#autoSelectSingleOptions();

          // We grab the variant object from the response and dispatch an event with it.
          // Dispatch null when not all options are explicitly selected — keeps buy button disabled.
          if (this.selectedOptionId) {
            const resource = this.#allOptionsSelected ? JSON.parse(textContent) : null;
            // Get minimum price from the product for "from" display
            const priceMin = this.#allOptionsSelected ? null : this.dataset.priceMin || null;
            this.dispatchEvent(
              new VariantUpdateEvent(resource, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                allOptionsSelected: this.#allOptionsSelected,
                priceMin,
                firstUnselectedOptionName: this.#firstUnselectedOptionName,
              })
            );
          }

          // Re-apply after the morph microtask updates refs, so the final DOM state is correct.
          // Also re-dispatch the VariantUpdateEvent with the authoritative post-morph state —
          // the sync dispatch above may have had stale #userSelectedFieldsets if the morph
          // triggered MutationObserver ref-refresh or auto-select lost a race, causing
          // #allOptionsSelected to be wrong at that moment. This deferred dispatch corrects it.
          requestYieldCallback(() => {
            if (noPreselect) {
              this.#applyNoPreselectState();
            } else {
              // Still apply force unselection in the callback for non-no-preselect products
              this.#applyForceUnselectionState();
            }
            this.#autoSelectSingleOptions();

            if (this.selectedOptionId) {
              const resource = this.#allOptionsSelected ? JSON.parse(textContent) : null;
              const priceMin = this.#allOptionsSelected ? null : this.dataset.priceMin || null;
              this.dispatchEvent(
                new VariantUpdateEvent(resource, this.selectedOptionId, {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct,
                  allOptionsSelected: this.#allOptionsSelected,
                  priceMin,
                  firstUnselectedOptionName: this.#firstUnselectedOptionName,
                })
              );
            }
          });
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
